import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { advance, skipWalk, startWalk } from './walk';
import type { Phase, Walk } from './walk';
import { landingOf, tourStops } from './tour-script';
import type { TourStop } from './tour-script';
import { useTour } from './useTour';
import type { Zone } from '@versesverse/core';

/*
 * THE ARRIVAL, AS ONE MACHINE.
 *
 * Three phases, a camera and a fog, and every one of them has to agree about where the reader is.
 * Spread across the effects of three components that is three machines which will eventually
 * disagree — the narration still up while the map is open, the fog lifted while the reader reads.
 * So the whole of it lives here and the components are told what to draw.
 *
 * IT COUNTS NOTHING OF ITS OWN. Whether a reader has been here comes from the trail, which is the
 * only record that survives closing the tab; this holds the phase and nothing else that could
 * drift from it.
 */

/**
 * How long the dark takes to close in around the reader, once the flight has landed.
 *
 * The design's 1.8 seconds, and the beat the whole arrival is built toward — reversed from where
 * the design put it. There, the fog lifted at the end of a survey to reveal a field the reader had
 * not seen. Here they have just been flown across the whole of it, and it goes out around them as
 * they land, which is what makes "the rest stays dark until you go there" a thing they watched
 * rather than a thing they were told.
 */
const FOG_CLOSE_MS = 1800;

export interface Arrival {
  walk: Walk;
  /** The beat of the flight in force, or null when it is not running. */
  stop: TourStop | null;
  /** How much of the fog is in force: 0 while the flight holds the whole field open, 1 once landed. */
  fogMix: number;
  /** Whether the map's own chrome should be showing yet. */
  open: boolean;
  /** Where the flight left the reader standing, or null before it lands. */
  landed: Zone | null;
  /** Move on from the landing — the reader has seen the name and wants the field. */
  goOn: () => void;
  /** Cut it short. There is always a way out, from the first second. */
  skip: () => void;
}

/**
 * Run the arrival.
 *
 * `places` are the derived neighbourhoods the flight visits and lands on; `rim` is a point out
 * past the outermost names, which the flight passes on its way in. `opened` is how many figures
 * the reader has ever opened, so somebody who has been here is never flown in again.
 */
export function useArrival(places: readonly Zone[], rim: { x: number; y: number }, opened: number, asked = false): Arrival {
  /* Read ONCE. The count climbs as the reader reads, and re-deriving the phase from it would fly
     somebody back out to the whole field the moment their first figure was recorded. */
  const [phase, setPhase] = useState<Phase>(() => startWalk(opened, asked).phase);
  const [fogMix, setFogMix] = useState(1);
  const [landed, setLanded] = useState<Zone | null>(null);
  const ramp = useRef<number | undefined>(undefined);

  const walk = useMemo<Walk>(() => ({ phase }), [phase]);

  /* The flight opens the whole field: the fog is what the reader is about to watch close in, and
     it cannot be lifted after the fact. */
  useEffect(() => {
    if (phase === 'tour') setFogMix(0);
  }, [phase]);

  /**
   * The dark closing in. Eased here rather than in the engine, because the engine would have to
   * keep a frame loop alive for a layer that changes twice in a reader's lifetime; a ramp driven
   * from here is thirty messages over 1.8 seconds and then silence.
   */
  const closeFog = useCallback(() => {
    const start = performance.now();
    const step = (): void => {
      const k = Math.min(1, (performance.now() - start) / FOG_CLOSE_MS);
      setFogMix(k);
      if (k < 1) ramp.current = requestAnimationFrame(step);
    };
    ramp.current = requestAnimationFrame(step);
  }, []);

  useEffect(() => () => { if (ramp.current !== undefined) cancelAnimationFrame(ramp.current); }, []);

  const stops = useMemo(() => tourStops(places, rim), [places, rim]);
  const stop = useTour(phase === 'tour', stops, () => {
    setPhase(p => (p === 'tour' ? advance({ phase: p }).phase : p));
    setLanded(landingOf(places));
    closeFog();
  });

  const goOn = useCallback(() => { setPhase(p => advance({ phase: p }).phase); }, []);
  const skip = useCallback(() => {
    setPhase(skipWalk().phase);
    /* A reader who left the arrival was never shown the field, so nothing closes in around them —
       they get the map they asked for, whole, and the fog begins from their next step. */
    setFogMix(1);
    setLanded(null);
  }, []);

  return { walk, stop, fogMix, open: phase === 'map', landed, goOn, skip };
}
